import { useCallback, useEffect, useMemo, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { useDashboardData } from "./useDashboardData";
import { useNotices } from "./useNotices";
import { useCircles } from "./useCircles";
import { useFeatureFlags } from "./useFeatureFlags";
import { api, Notice } from "../services/api";
import { syncTermsFromBackend } from "../utils/termsAcceptance";
import { runOncePerSession } from "../utils/oncePerSession";
import { useAdvertisements } from "./useAdvertisements";
import { getDashboardDisplayState } from "../domain/dashboard/dashboardConditions";
import {
  computePublicPageStatsUpdate,
  type PublicPageSettings,
  type PublicPageStats,
} from "../domain/dashboard/publicPageStatsSync";
import { bucketNoticesByLastNDays } from "../domain/dashboard/noticeDayBuckets";
import { isAdActive } from "../domain/dashboard/advertisementActivity";

export const useDashboardState = () => {
  const { user, token, updateUser } = useAuth();
  const { data, isLoading, error, refetch } = useDashboardData();
  const {
    data: notices,
    isLoading: isLoadingNotices,
    error: noticesError,
    refetch: refetchNotices,
  } = useNotices();
  const { data: circles = [] } = useCircles();
  const { isAdCenterEnabled } = useFeatureFlags();

  // `error` below can come from either query, so a retry has to re-run both.
  const refetchAll = useCallback(() => {
    refetch();
    refetchNotices();
  }, [refetch, refetchNotices]);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Sync terms acceptances from backend once per session per Organization;
  // a failed sync retries on the next visit, and localStorage is used meanwhile.
  useEffect(() => {
    const orgId = user?.schoolId || user?.orgId;
    if (token && orgId) {
      const org = String(orgId);
      runOncePerSession("termsSync", org, () => syncTermsFromBackend(org, token));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch advertisements to count active ones.
  const { data: advertisementsData, isLoading: isLoadingAdverts } =
    useAdvertisements({ enabled: isAdCenterEnabled });

  // Count active advertisements (published, not cancelled, not expired)
  const activeAdverts = useMemo(() => {
    if (!advertisementsData) return 0;
    const now = new Date();
    return advertisementsData.filter(
      (ad) => !ad.isDraft && !ad.isDeleted && isAdActive(ad, now)
    ).length;
  }, [advertisementsData]);

  // Draft notices
  const draftNotices = useMemo(() => {
    if (!notices) return [];
    return notices.filter((n: Notice) => n.isDraft && !n.isDeleted);
  }, [notices]);

  // Check if user needs onboarding
  const needsOnboarding =
    user?.userType !== "team" &&
    user?.isSchoolApproved &&
    (!user?.schoolLogo ||
      user.schoolLogo === "" ||
      user.schoolLogo === "/placeholder.jpg" ||
      !user?.schoolDescription ||
      user.schoolDescription === "" ||
      user.schoolDescription.includes("setup your Noticeboard") ||
      !user?.orgBanner ||
      user.orgBanner === "" ||
      !user?.phoneNumber ||
      user.phoneNumber === "");

  // Chart data (last 7 days)
  const chartTitle = "Notices Created (Last 7 Days)";
  const filteredNoticesByDay = useMemo(() => {
    if (!notices || notices.length === 0) return [];
    return bucketNoticesByLastNDays(notices, 7);
  }, [notices]);

  // Upcoming events
  const upcomingEvents = data?.upcomingEvents || 0;

  // Calculate active notices count (non-draft, non-deleted)
  const activeNoticesCount = useMemo(() => {
    if (!notices) return 0;
    return notices.filter((n: Notice) => !n.isDraft && !n.isDeleted).length;
  }, [notices]);

  // Get last 3 active advertisements for public page
  const publicAdvertisements = useMemo(() => {
    if (!advertisementsData) return [];
    const now = new Date();

    return advertisementsData
      .filter((ad) => !ad.isDraft && !ad.isDeleted && isAdActive(ad, now))
      .sort((a, b) => {
        // Sort by createdAt descending (newest first)
        const dateA = new Date(a.createdAt).getTime();
        const dateB = new Date(b.createdAt).getTime();
        return dateB - dateA;
      })
      .slice(0, 3) // Get last 3
      .map((ad) => ({
        advertisementId: ad.advertisementId,
        title: ad.title,
        description: ad.description,
        price: ad.price,
        category: ad.category,
        adType: ad.adType,
        imageUrl: ad.photos?.[0] || ad.bannerImage,
        location: ad.location,
        createdAt: ad.createdAt,
        expiresAt: ad.expiresAt,
      }));
  }, [advertisementsData]);

  // Sync stats and advertisements to publicPageSettings when stats are fetched (on login/dashboard load)
  useEffect(() => {
    // Wait for all required data to be loaded
    if (!data || !user?.schoolId || !token || isLoading || isLoadingNotices) return;
    
    // Only sync if public page is enabled
    if (user?.isPublic !== true && user?.isPublic !== "true") {
      return;
    }

    // Orgs without Ad Center never fetch ads; they sync an empty list.
    if (isAdCenterEnabled && advertisementsData === undefined) {
      return;
    }

    const schoolId = String(user.schoolId);

    // Once per session per Organization: stats that change mid-session reach
    // the public page on a later session (accepted trade-off). Resolves true
    // when done, including when nothing needed changing. A failed write
    // rejects, which runOncePerSession treats as "retry next visit"; it's a
    // non-blocking background sync, so the dashboard works either way.
    const syncStats = async (): Promise<boolean> => {
      const currentSettings = (user?.publicPageSettings ||
        {}) as PublicPageSettings;
      const newStats: PublicPageStats = {
        parentCount: data.totalUsers || 0,
        activeNoticeCount: activeNoticesCount,
        upcomingEventsCount: upcomingEvents,
        advertisements: publicAdvertisements || [],
      };

      const { needsUpdate, updatedSettings } = computePublicPageStatsUpdate(
        currentSettings,
        newStats
      );

      if (!needsUpdate) {
        return true;
      }

      await api.dashboard.updateAccountDetails(
        {
          schoolId,
          publicPageSettings: updatedSettings,
        },
        token
      );

      // Update user object silently (don't trigger re-render spam)
      // IMPORTANT: Use functional update to ensure we have the latest user state
      // This prevents race conditions where isPublic might be overwritten
      updateUser((prevUser: Record<string, unknown> | null): Record<string, unknown> | null => {
        if (!prevUser) return prevUser;
        const updated = {
          ...prevUser,
          publicPageSettings: updatedSettings,
          // Explicitly preserve isPublic to prevent it from being lost during stats sync
          isPublic: prevUser.isPublic,
        };
        return updated;
      });
      return true;
    };

    runOncePerSession("publicPageStats", schoolId, syncStats);
    // Note: We intentionally exclude some dependencies to prevent race conditions:
    // - user?.isPublic: We don't want to sync when isPublic changes (that's handled separately)
    // - user?.publicPageSettings: We're updating this, so including it would cause loops
    // - updateUser: Stable function reference, doesn't need to be in deps
    // - token, isLoading, isLoadingNotices: These are stable or checked inside the effect
    // - advertisementsData: We use publicAdvertisements (derived from it) instead
    // - data: We use specific fields (data?.totalUsers, data?.upcomingEvents) instead
    // - upcomingEvents: We use data?.upcomingEvents instead
    // - user.schoolId: Checked inside the effect, and we don't want to re-run on every user change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.totalUsers, data?.upcomingEvents, activeNoticesCount, publicAdvertisements]);

  // Dashboard display conditions
  const dashboardState = useMemo(() => {
    const hasNotices = Boolean(notices && notices.length > 0);
    const hasAdverts = activeAdverts > 0;

    return {
      hasNotices,
      hasAdverts,
      isLoadingNotices,
      isLoadingAdverts,
    };
  }, [notices, activeAdverts, isLoadingNotices, isLoadingAdverts]);

  const display = useMemo(() => getDashboardDisplayState(dashboardState), [dashboardState]);

  // Subcircles check (need at least 2 circles: organization + 1 subcircle)
  const hasSubcircles = circles.length >= 2;

  // Refresh org settings on Home and keep polling while approval is pending.
  useEffect(() => {
    if (
      user?.userType !== "team" &&
      user?.schoolId &&
      token
    ) {
      const schoolId = user.schoolId as string;
      const checkApprovalStatus = async () => {
        try {
          const result = await api.users.checkApprovalStatus(schoolId, token);
          if (result.isApproved) {
            // Functional update: reads the latest user rather than the
            // possibly-stale snapshot this closure captured, since this
            // effect deliberately doesn't depend on the whole user object.
            updateUser((prevUser) =>
              prevUser && prevUser.schoolId === schoolId
                ? { ...prevUser, isSchoolApproved: true, ...(result.featureFlags !== undefined ? { featureFlags: result.featureFlags } : {}) }
                : prevUser
            );
            if (pollingIntervalRef.current) {
              clearInterval(pollingIntervalRef.current);
              pollingIntervalRef.current = null;
            }
          }
        } catch {
          // Polling will retry.
        }
      };

      checkApprovalStatus();
      if (!user?.isSchoolApproved) pollingIntervalRef.current = setInterval(checkApprovalStatus, 10000);

      return () => {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
      };
    } else {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    }
    // Deliberately depend only on the specific fields that decide whether to
    // poll — not the whole user object — so an unrelated user update (logo/
    // banner/phone/public-page sync) doesn't tear down and restart the poll.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.userType, user?.isSchoolApproved, user?.schoolId, token]);

  return {
    // Raw data
    data,
    notices,
    circles,
    activeAdverts,
    draftNotices,
    upcomingEvents,

    // Computed state
    display,
    hasSubcircles,
    needsOnboarding,
    filteredNoticesByDay,
    chartTitle,

    // Loading / error — wait for stats and notices before leaving the full-page skeleton
    isLoading: isLoading || isLoadingNotices,
    error: error || noticesError,
    refetch: refetchAll,
  };
};
